import { useTranslation } from "react-i18next";

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "#app/components/ui/pagination";
import { useIsMobile } from "#app/hooks/use-mobile";
import { cn } from "#app/utils/misc.tsx";

type PaginationBlockProps = {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Optional range label, e.g. "1-12 of 40". */
  leading?: React.ReactNode;
  className?: string;
};

/**
 * Builds the page list with at most one ellipsis on each side, e.g.
 * `1 … 4 5 6 … 20`. Compact mode keeps first / current / last for narrow
 * viewports so controls wrap instead of overflowing.
 */
function buildPages(
  page: number,
  totalPages: number,
  compact = false,
): Array<number | "gap"> {
  if (totalPages <= 1) return [1];
  if (compact) {
    const pages = new Set([1, totalPages, page]);
    const visible = [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
    const out: Array<number | "gap"> = [];
    let previous = 0;
    for (const p of visible) {
      if (previous && p - previous > 1) out.push("gap");
      out.push(p);
      previous = p;
    }
    return out;
  }

  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set([1, totalPages, page, page - 1, page + 1]);
  const visible = [...pages]
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);

  const out: Array<number | "gap"> = [];
  let previous = 0;
  for (const p of visible) {
    if (previous && p - previous > 1) out.push("gap");
    out.push(p);
    previous = p;
  }
  return out;
}

/**
 * Shared list pagination — shadcn `Pagination` primitives, in normal document
 * flow (not sticky / fixed). Sits under the list and reflows on small screens.
 */
export function PaginationBlock({
  page,
  totalPages,
  onPageChange,
  leading,
  className,
}: PaginationBlockProps) {
  const { t } = useTranslation("common");
  const isMobile = useIsMobile();
  const showControls = totalPages > 1;

  if (totalPages <= 1 && !leading) return null;

  const go = (target: number) => (event: React.MouseEvent) => {
    event.preventDefault();
    const next = Math.min(Math.max(target, 1), totalPages);
    if (next !== page) onPageChange(next);
  };

  const pageEntries = buildPages(page, totalPages, isMobile);

  return (
    <div
      className={cn(
        "relative w-full min-w-0 max-w-full overflow-x-hidden px-4 py-4 sm:px-6",
        !showControls && "sm:flex sm:items-center",
        showControls && leading
          ? // Range left, controls centered in the full bar (prod Audit Trail).
            "flex flex-col items-center gap-3 sm:block sm:min-h-10"
          : showControls
            ? "flex flex-wrap items-center justify-center gap-2"
            : null,
        className,
      )}
    >
      {leading ? (
        <span
          className={cn(
            "min-w-0 truncate text-center text-xs text-muted-foreground sm:text-left sm:text-sm",
            showControls &&
              "sm:absolute sm:top-1/2 sm:left-4 sm:z-10 sm:max-w-[40%] sm:-translate-y-1/2 sm:px-0 lg:left-6",
          )}
        >
          {leading}
        </span>
      ) : null}

      {showControls ? (
        <Pagination className="mx-0 w-full min-w-0 max-w-full justify-center">
          <PaginationContent className="flex min-w-0 max-w-full flex-wrap justify-center gap-1">
            <PaginationItem>
              <PaginationPrevious
                text={t("ui.previous")}
                aria-label={t("ui.goToPreviousPage")}
                aria-disabled={page === 1}
                className={cn(page === 1 && "pointer-events-none opacity-50")}
                onClick={go(page - 1)}
              />
            </PaginationItem>

            {pageEntries.map((entry, index) =>
              entry === "gap" ? (
                <PaginationItem key={`gap-${index}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={entry}>
                  <PaginationLink
                    isActive={entry === page}
                    aria-label={t("ui.goToPageNumber", { page: entry })}
                    onClick={go(entry)}
                  >
                    {entry}
                  </PaginationLink>
                </PaginationItem>
              ),
            )}

            <PaginationItem>
              <PaginationNext
                text={t("ui.next")}
                aria-label={t("ui.goToNextPage")}
                aria-disabled={page === totalPages}
                className={cn(
                  page === totalPages && "pointer-events-none opacity-50",
                )}
                onClick={go(page + 1)}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      ) : null}
    </div>
  );
}

export default PaginationBlock;
